import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Map as MapLibreMap, Marker, Popup } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "@/lib/maplibreWorker";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Clock,
  Copy,
  HeartPulse,
  Loader2,
  MapPin,
  MessageSquare,
  Navigation as NavigationIcon,
  RefreshCw,
  Send,
  ShieldAlert,
  TrafficCone,
  User,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { supabase, supabaseConfigured } from "@/lib/supabase";
import { MAP_STYLE_URL, MAP_STYLE_CONFIG_ERROR, MAP_STYLE_LOAD_ERROR_MESSAGE } from "@/lib/mapStyle";
import { getRoutePointMeta } from "@/lib/routePointIcons";

// Sprint 09H-B — Admin Incident Center. Reuses the existing ticket-admin shell (dark
// svj-* theme, useAuth()'s authAxios, sonner toasts) exactly like every other admin
// page in this file tree — no new admin platform, no new login.

const QUICK_REPLY_FALLBACK = [
  "We hebben je melding gezien.",
  "EHBO is onderweg.",
  "Blijf indien mogelijk op deze locatie.",
  "Bel ons even via de Hulp-knop.",
  "Hulpvraag is afgehandeld.",
];

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("nl-NL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function StatusBadge({ status, resolutionReason }) {
  if (status === "open") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/30 px-2.5 py-1 text-xs font-bold uppercase tracking-wide">
        <span className="h-1.5 w-1.5 rounded-full bg-red-400 animate-pulse" /> Open
      </span>
    );
  }
  const label = resolutionReason === "misuse" ? "Onterecht" : "Opgelost";
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-2.5 py-1 text-xs font-bold uppercase tracking-wide">
      <Check className="h-3 w-3" /> {label}
    </span>
  );
}

// --- List -----------------------------------------------------------------------

export function AdminIncidentsOverview() {
  const { authAxios, me } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("open");
  const [resolutionReason, setResolutionReason] = useState("");
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await authAxios.get("/admin/incidents", {
        params: {
          event_id: eventId || undefined,
          status: status || undefined,
          resolution_reason: resolutionReason || undefined,
        },
      });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [authAxios, eventId, status, resolutionReason]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await authAxios.get("/admin/events", { params: { companion_app_enabled: true } });
        setEvents(data.items || []);
      } catch {
        setEvents([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  // §35 — Supabase Realtime, admin-only (a genuine authenticated Supabase Auth
  // session, RLS-scoped via is_org_member()). A full refetch on any change rather
  // than hand-rolled delta merging: simpler and just as "vrijwel direct zichtbaar".
  useEffect(() => {
    if (!supabaseConfigured) return undefined;
    const orgId = me?.organization?.id;
    const filter = !me?.is_superadmin && orgId ? `organization_id=eq.${orgId}` : undefined;
    const channel = supabase
      .channel(`admin-incidents-list-${orgId || "all"}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "event_incidents", filter }, () => load())
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.organization?.id, me?.is_superadmin, load]);

  return (
    <div data-testid="admin-incidents-page">
      <div className="text-xs uppercase tracking-widest text-svj-cyan font-semibold">Beheer</div>
      <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold text-white">Incidenten</h1>
      <p className="mt-1 text-sm text-slate-400">Hulpvragen van deelnemers via "Meld probleem of ongeval".</p>

      <div className="mt-6 flex flex-wrap gap-2">
        {[
          { key: "open", label: "Open" },
          { key: "resolved_resolved", label: "Opgelost" },
          { key: "resolved_misuse", label: "Onterecht" },
          { key: "", label: "Alle" },
        ].map((f) => {
          const active =
            f.key === "" ? status === "" :
            f.key === "open" ? status === "open" :
            f.key === "resolved_resolved" ? status === "resolved" && resolutionReason === "resolved" :
            status === "resolved" && resolutionReason === "misuse";
          return (
            <button
              key={f.key}
              type="button"
              data-testid={`admin-incidents-filter-${f.key || "all"}`}
              onClick={() => {
                if (f.key === "open") { setStatus("open"); setResolutionReason(""); }
                else if (f.key === "resolved_resolved") { setStatus("resolved"); setResolutionReason("resolved"); }
                else if (f.key === "resolved_misuse") { setStatus("resolved"); setResolutionReason("misuse"); }
                else { setStatus(""); setResolutionReason(""); }
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                active ? "bg-svj-orange/20 border-svj-orange text-white" : "border-svj-border text-slate-300 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          );
        })}
        <select
          value={eventId}
          onChange={(e) => setEventId(e.target.value)}
          data-testid="admin-incidents-filter-event"
          className="ml-auto bg-svj-card border border-svj-border rounded-full px-3 py-1.5 text-xs text-white"
        >
          <option value="">Alle events</option>
          {events.map((ev) => (
            <option key={ev.id} value={ev.id}>{ev.title}</option>
          ))}
        </select>
      </div>

      <div className="mt-4 space-y-3">
        {loading && <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Laden...</div>}
        {!loading && items.length === 0 && <div className="text-slate-500 text-sm">Geen hulpvragen gevonden.</div>}
        {items.map((item) => (
          <Link
            key={item.id}
            to={`/admin/incidents/${item.id}`}
            data-testid={`admin-incident-row-${item.id}`}
            className="block bg-svj-card border border-svj-border rounded-2xl p-4 hover:border-svj-orange/50 transition-colors"
          >
            <div className="flex items-center justify-between gap-3">
              <StatusBadge status={item.status} resolutionReason={item.resolution_reason} />
              <span className="text-xs text-slate-500">{formatTime(item.created_at)}</span>
            </div>
            <div className="mt-2 flex items-center gap-2 text-white font-bold">
              {item.reporter_type === "traffic_controller" ? (
                <TrafficCone className="h-4 w-4 text-amber-400" />
              ) : (
                <User className="h-4 w-4 text-svj-cyan" />
              )}
              {item.participant_name || "Onbekende deelnemer"}
              {item.reporter_type === "traffic_controller" && (
                <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5">
                  Verkeersregelaar
                </span>
              )}
            </div>
            <div className="mt-0.5 text-xs text-slate-400">
              {[item.route_title, item.route_distance_km ? `${item.route_distance_km} km` : null, item.day_label].filter(Boolean).join(" · ") || "Geen route/dag"}
            </div>
            {item.last_message_preview && (
              <div className="mt-2 text-sm text-slate-300 line-clamp-2">"{item.last_message_preview}"</div>
            )}
            <div className="mt-3 flex items-center gap-4 text-[11px] text-slate-500">
              {item.has_location && (
                <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> Locatie beschikbaar</span>
              )}
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Laatste activiteit {formatTime(item.last_activity_at)}</span>
              <span className="inline-flex items-center gap-1"><MessageSquare className="h-3 w-3" /> {item.message_count} bericht{item.message_count === 1 ? "" : "en"}</span>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

// --- Detail ------------------------------------------------------------------------

export function AdminIncidentDetail() {
  const { incidentId } = useParams();
  const navigate = useNavigate();
  const { authAxios, me } = useAuth();
  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [composer, setComposer] = useState("");
  const [sending, setSending] = useState(false);
  const [quickReplies, setQuickReplies] = useState(QUICK_REPLY_FALLBACK);
  const [resolving, setResolving] = useState(false);

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const [mapError, setMapError] = useState(MAP_STYLE_CONFIG_ERROR);

  const load = useCallback(async () => {
    try {
      const { data } = await authAxios.get(`/admin/incidents/${incidentId}`);
      setIncident(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [authAxios, incidentId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  useEffect(() => {
    authAxios.get("/admin/incidents/quick-replies").then(({ data }) => {
      if (data?.templates?.length) setQuickReplies(data.templates);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // §35/§83 — Realtime for this one incident's row + its message timeline.
  useEffect(() => {
    if (!supabaseConfigured || !incidentId) return undefined;
    const channel = supabase
      .channel(`admin-incident-${incidentId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "event_incidents", filter: `id=eq.${incidentId}` }, () => load())
      .on("postgres_changes", { event: "*", schema: "public", table: "event_incident_messages", filter: `incident_id=eq.${incidentId}` }, () => load())
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidentId, load]);

  // §29/§30/§33/§34 — the incident map: red pin, canonical route line (fetched live,
  // never copied into the incident row), and first_aid POIs reusing the existing
  // shared icon/color registry. Never constructed when there's no location.
  useEffect(() => {
    if (!incident || incident.latitude == null || incident.longitude == null || !mapContainerRef.current) return undefined;
    if (MAP_STYLE_CONFIG_ERROR) {
      setMapError(MAP_STYLE_CONFIG_ERROR);
      return undefined;
    }
    setMapError(null);
    const map = new MapLibreMap({
      container: mapContainerRef.current,
      style: MAP_STYLE_URL,
      center: [incident.longitude, incident.latitude],
      zoom: 15,
    });
    mapRef.current = map;
    map.on("error", () => setMapError(MAP_STYLE_LOAD_ERROR_MESSAGE));

    new Marker({ color: "#dc2626" })
      .setLngLat([incident.longitude, incident.latitude])
      .setPopup(new Popup({ offset: 12 }).setText("Locatie hulpvraag"))
      .addTo(map);

    if (incident.nearest_first_aid) {
      const meta = getRoutePointMeta("first_aid");
      new Marker({ color: meta.color, scale: 0.75 })
        .setLngLat([incident.nearest_first_aid.longitude, incident.nearest_first_aid.latitude])
        .setPopup(new Popup({ offset: 10 }).setText(`EHBO: ${incident.nearest_first_aid.title} (${incident.nearest_first_aid.distance_meters} m hemelsbreed)`))
        .addTo(map);
    }

    if (incident.route_id) {
      authAxios
        .get(`/admin/routes/${incident.route_id}/navigation`)
        .then(({ data }) => {
          if (!data?.geojson || !mapRef.current) return;
          const drawLine = () => {
            if (mapRef.current.getSource("incident-route-line")) return;
            mapRef.current.addSource("incident-route-line", { type: "geojson", data: data.geojson });
            mapRef.current.addLayer({
              id: "incident-route-line",
              type: "line",
              source: "incident-route-line",
              paint: { "line-color": "#16a34a", "line-width": 4, "line-opacity": 0.85 },
            });
          };
          if (mapRef.current.isStyleLoaded()) drawLine();
          else mapRef.current.on("load", drawLine);
        })
        .catch(() => {});
    }

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident?.id, incident?.latitude, incident?.longitude]);

  const sendMessage = async (text) => {
    const message = (text || composer).trim();
    if (!message) return;
    setSending(true);
    try {
      const { data } = await authAxios.post(`/admin/incidents/${incidentId}/messages`, {
        message,
        client_message_id: crypto.randomUUID(),
      });
      toast.success(data.detail || "Bericht verstuurd");
      setComposer("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSending(false);
    }
  };

  const resolve = async (resolution_reason) => {
    setResolving(true);
    try {
      const finalMessage = resolution_reason === "resolved" && composer.trim() ? composer.trim() : undefined;
      const { data } = await authAxios.post(`/admin/incidents/${incidentId}/resolve`, {
        resolution_reason,
        message: finalMessage,
      });
      toast.success(resolution_reason === "misuse" ? "Gemarkeerd als onterecht" : "Hulpvraag opgelost");
      if (data.resolved) setComposer("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setResolving(false);
    }
  };

  if (loading && !incident) {
    return <div className="text-slate-400 text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Laden...</div>;
  }
  if (!incident) return null;

  return (
    <div data-testid="admin-incident-detail-page" className="max-w-4xl">
      <button type="button" onClick={() => navigate("/admin/incidents")} className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white">
        <ArrowLeft className="h-3.5 w-3.5" /> Terug naar incidenten
      </button>

      <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
        <div>
          <StatusBadge status={incident.status} resolutionReason={incident.resolution_reason} />
          <h1 className="mt-2 text-2xl font-extrabold text-white flex items-center gap-2">
            {incident.reporter_type === "traffic_controller" ? (
              <TrafficCone className="h-5 w-5 text-amber-400" />
            ) : (
              <User className="h-5 w-5 text-svj-cyan" />
            )}
            {incident.reporter_type === "traffic_controller"
              ? incident.controller?.display_name || "Onbekende verkeersregelaar"
              : incident.ticket?.holder_name || "Onbekende deelnemer"}
            {incident.reporter_type === "traffic_controller" && (
              <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5">
                Verkeersregelaar{incident.controller?.role === "sluiter" ? " · sluiter" : ""}
              </span>
            )}
          </h1>
          <div className="text-xs text-slate-400 mt-1">
            {incident.event?.title} · {incident.route?.title ? `${incident.route.title}${incident.route.distance_km ? ` (${incident.route.distance_km} km)` : ""}` : "Geen route"} · {incident.day?.label || (incident.day?.day_number ? `Dag ${incident.day.day_number}` : "Geen dag")}
          </div>
          <div className="text-xs text-slate-500 mt-1">Eerste melding {formatTime(incident.created_at)} · Laatste activiteit {formatTime(incident.last_activity_at)} · {incident.report_count} melding{incident.report_count === 1 ? "" : "en"}</div>
          {incident.route_id && incident.event?.id && (
            <Link
              to={`/admin/events/${incident.event.id}?section=stapkompas`}
              data-testid="admin-incident-open-route"
              title="Open de route in StapKompas om een aanpassing te maken (bijv. een extra verkeerspost)"
              className="mt-2 inline-flex items-center gap-1.5 text-xs text-svj-cyan hover:text-white"
            >
              <TrafficCone className="h-3.5 w-3.5" /> Open route in StapKompas
            </Link>
          )}
        </div>
        {incident.status === "open" && (
          <div className="flex gap-2">
            <button type="button" disabled={resolving} onClick={() => resolve("resolved")} data-testid="admin-incident-resolve" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-bold px-4 py-2">
              <Check className="h-3.5 w-3.5" /> Markeer opgelost
            </button>
            <button type="button" disabled={resolving} onClick={() => resolve("misuse")} data-testid="admin-incident-misuse" className="inline-flex items-center gap-1.5 rounded-full border border-svj-border text-slate-300 hover:text-white disabled:opacity-50 text-xs font-bold px-4 py-2">
              <ShieldAlert className="h-3.5 w-3.5" /> Markeer onterecht
            </button>
          </div>
        )}
      </div>

      {/* Location + map (§29-§32) */}
      {incident.latitude != null && incident.longitude != null ? (
        <div className="mt-6 bg-svj-card border border-svj-border rounded-2xl overflow-hidden">
          <div className="relative h-64">
            <div className="absolute inset-0">
              <div ref={mapContainerRef} className="w-full h-full" data-testid="admin-incident-map" />
            </div>
            {mapError && (
              <div className="absolute inset-0 flex items-center justify-center bg-svj-bg p-3">
                <div className="flex items-start gap-2 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 max-w-sm">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {mapError}
                </div>
              </div>
            )}
          </div>
          <div className="p-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <div className="font-mono text-slate-300">{incident.latitude.toFixed(6)}, {incident.longitude.toFixed(6)}</div>
            {incident.location_accuracy_m != null && <div className="text-slate-400 text-xs">Nauwkeurigheid ±{Math.round(incident.location_accuracy_m)} m</div>}
            {incident.nearest_first_aid && (
              <div className="text-slate-400 text-xs flex items-center gap-1">
                <HeartPulse className="h-3.5 w-3.5 text-red-400" /> Dichtstbijzijnde EHBO: {incident.nearest_first_aid.title} ({incident.nearest_first_aid.distance_meters} m hemelsbreed)
              </div>
            )}
            <button
              type="button"
              data-testid="admin-incident-copy-location"
              onClick={() => {
                navigator.clipboard?.writeText(`${incident.latitude}, ${incident.longitude}`);
                toast.success("Locatie gekopieerd");
              }}
              className="inline-flex items-center gap-1.5 text-xs text-svj-cyan hover:text-white"
            >
              <Copy className="h-3.5 w-3.5" /> Kopieer locatie
            </button>
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${incident.latitude},${incident.longitude}`}
              target="_blank"
              rel="noreferrer"
              data-testid="admin-incident-navigate"
              className="inline-flex items-center gap-1.5 text-xs text-svj-cyan hover:text-white"
            >
              <NavigationIcon className="h-3.5 w-3.5" /> Navigeer naar incident
            </a>
          </div>
        </div>
      ) : (
        <div className="mt-6 text-sm text-slate-500 bg-svj-card border border-svj-border rounded-2xl p-4">Geen locatie meegestuurd bij deze hulpvraag.</div>
      )}

      {/* Timeline (§13/§28/§52) */}
      <div className="mt-6 bg-svj-card border border-svj-border rounded-2xl p-4">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Tijdlijn</div>
        <div className="space-y-3" data-testid="admin-incident-timeline">
          {(incident.messages || []).map((m) => (
            <div key={m.id} className={`rounded-xl px-3 py-2 text-sm ${m.sender_type === "organizer" ? "bg-svj-cyan/10 border border-svj-cyan/30 ml-6" : "bg-svj-bg border border-svj-border mr-6"}`}>
              <div className="flex items-center justify-between gap-3 text-[11px] text-slate-500 mb-1">
                <span className="font-bold">
                  {m.sender_type === "organizer"
                    ? "Organisatie"
                    : m.sender_type === "system"
                      ? "Systeem"
                      : m.sender_type === "traffic_controller"
                        ? incident.controller?.display_name || "Verkeersregelaar"
                        : incident.ticket?.holder_name || "Deelnemer"}
                </span>
                <span>{formatTime(m.created_at)}</span>
              </div>
              <div className="text-slate-200">{m.message}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Push status (§9 production fix — was fetched by the backend since launch,
          never rendered here, so production troubleshooting was blind) */}
      {(incident.push_attempts || []).length > 0 && (
        <div className="mt-6 bg-svj-card border border-svj-border rounded-2xl p-4">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Pushstatus</div>
          <div className="space-y-2" data-testid="admin-incident-push-attempts">
            {incident.push_attempts.map((a, i) => (
              <div key={i} className="flex items-center justify-between gap-3 text-sm bg-svj-bg border border-svj-border rounded-lg px-3 py-2">
                <div>
                  <div className="font-semibold text-white">
                    {a.audience === "organizer" ? "Organisatie" : a.audience === "traffic_controller" ? "Verkeersregelaar" : "Deelnemer"}
                    <span className="text-slate-500 font-normal"> · {formatTime(a.attempted_at)}</span>
                  </div>
                  <div className="text-xs text-slate-400">
                    {{ initial: "Nieuwe hulpvraag", participant_update: "Update van deelnemer", organizer_reply: "Antwoord organisatie", manual_retry: "Handmatige test" }[a.kind] || a.kind}
                  </div>
                </div>
                <span
                  className={`text-xs font-bold px-2 py-1 rounded-full ${
                    a.status === "accepted"
                      ? "bg-emerald-500/15 text-emerald-300"
                      : a.status === "no_recipients"
                      ? "bg-slate-500/15 text-slate-300"
                      : "bg-red-500/15 text-red-300"
                  }`}
                >
                  {a.status === "accepted" ? "Geaccepteerd" : a.status === "no_recipients" ? "Geen ontvangers" : a.status === "invalid_subscription" ? "Ongeldig abonnement" : `Mislukt${a.safe_error_code ? ` — ${a.safe_error_code}` : ""}`}
                </span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-slate-500">"Geaccepteerd" betekent dat de pushservice het bericht heeft geaccepteerd — niet dat het gelezen is.</p>
        </div>
      )}

      {/* Organizer reply (§47-§51) */}
      {incident.status === "open" && (
        <div className="mt-6 bg-svj-card border border-svj-border rounded-2xl p-4">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Bericht aan deelnemer</div>
          <div className="flex flex-wrap gap-2 mb-3">
            {quickReplies.map((q) => (
              <button
                key={q}
                type="button"
                data-testid={`admin-incident-quick-reply-${q.slice(0, 8)}`}
                onClick={() => setComposer(q)}
                className="text-xs px-3 py-1.5 rounded-full border border-svj-border text-slate-300 hover:text-white hover:border-svj-orange/50"
              >
                {q}
              </button>
            ))}
          </div>
          <textarea
            value={composer}
            onChange={(e) => setComposer(e.target.value.slice(0, 500))}
            rows={3}
            maxLength={500}
            data-testid="admin-incident-composer"
            placeholder="Typ een bericht aan de deelnemer..."
            className="w-full bg-svj-bg border border-svj-border rounded-xl px-3 py-2 text-sm text-white"
          />
          <div className="mt-2 flex items-center justify-between">
            <span className="text-[11px] text-slate-500">{composer.length}/500</span>
            <button
              type="button"
              disabled={sending || !composer.trim()}
              onClick={() => sendMessage()}
              data-testid="admin-incident-send"
              className="inline-flex items-center gap-1.5 rounded-full bg-svj-orange hover:bg-svj-orange/80 disabled:opacity-50 text-white text-xs font-bold px-4 py-2"
            >
              <Send className="h-3.5 w-3.5" /> Verstuur bericht
            </button>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={load}
        data-testid="admin-incident-refresh"
        className="mt-4 inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-white"
      >
        <RefreshCw className="h-3 w-3" /> Vernieuwen
      </button>
    </div>
  );
}
